import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Loader2, Hand, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import ToolShell from "../../components/ToolShell";
import { api } from "../../lib/api";

const LINES = [
  ["life_line", "Life Line"], ["heart_line", "Heart Line"], ["head_line", "Head Line"],
  ["fate_line", "Fate Line"], ["marriage_line", "Marriage Line"], ["sun_line", "Sun Line"],
  ["health_line", "Health Line"],
];

export default function PalmReading() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please upload an image file.");
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const analyze = async () => {
    if (!file) return toast.error("Please upload a palm image first.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tools/palm", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setTimeout(() => document.getElementById("palm-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Could not analyze the image. Please try a clearer palm photo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="palm-reading" name="AI Palm Reading" tagline="Upload a palm, meet the lines">
      <SEO title="AI Palm Reading" description="Upload a photo of your palm and let Numoraa's AI read your life, heart, head, fate, marriage, sun and health lines with warm guidance." path="/tools/palm-reading" />

      <div className="grid gap-8 md:grid-cols-2 max-w-4xl">
        <div>
          <label className="group relative flex aspect-[4/5] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-3xl border-2 border-dashed border-border bg-card transition-colors hover:border-primary" data-testid="palm-dropzone">
            {preview ? (
              <img src={preview} alt="Palm preview" className="h-full w-full object-cover" />
            ) : (
              <div className="text-center px-6">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary"><Upload className="h-6 w-6 text-primary" /></span>
                <p className="mt-4 font-serif text-xl">Upload your palm</p>
                <p className="mt-1 text-sm text-muted-foreground">Clear photo of your right palm, well lit.</p>
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="palm-file-input" />
          </label>
          <button onClick={analyze} disabled={loading || !file} data-testid="palm-analyze"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hand className="h-4 w-4 text-gold" />}
            {loading ? "Reading your palm…" : "Analyze my palm"}
          </button>
        </div>

        <div className="rounded-3xl border border-border bg-secondary/40 p-7">
          <h3 className="font-serif text-2xl">How it works</h3>
          <ol className="mt-4 space-y-3 text-sm text-muted-foreground">
            <li>1. Take a clear, well-lit photo of your open palm.</li>
            <li>2. Upload it and tap Analyze.</li>
            <li>3. Our AI studies seven major lines and shares practical, encouraging insights.</li>
          </ol>
          <div className="mt-6 flex gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4 text-xs text-foreground">
            <AlertTriangle className="h-4 w-4 shrink-0 text-gold" />
            <p>Palm reading results are AI-generated guidance for reflection and entertainment — not guaranteed predictions.</p>
          </div>
        </div>
      </div>

      {result && (
        <motion.div id="palm-result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-14 max-w-4xl" data-testid="palm-result">
          {result.overall && (
            <div className="rounded-3xl bg-primary text-primary-foreground p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Overall reading</p>
              <p className="mt-3 font-serif text-xl md:text-2xl leading-relaxed">{result.overall}</p>
            </div>
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {LINES.map(([key, label]) => (
              (result[key] && result[key].length > 0) && (
                <div key={key} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <h3 className="font-serif text-xl">{label}</h3>
                  <ul className="mt-3 space-y-2">
                    {result[key].map((it, i) => (
                      <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{it}</li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground italic">{result.disclaimer}</p>
        </motion.div>
      )}

      <section className="mt-20 max-w-3xl" data-testid="palm-explainer">
        <h2 className="font-serif text-3xl">What is palm reading?</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Palm reading, also called palmistry, is the traditional practice of looking at the lines and shapes of the hand and reading them as symbols for personality and life themes. It is a tool for reflection, not a science, and it cannot predict the future.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          This tool studies a photo of your palm with AI and shares the traditional meaning of seven major lines in a warm, practical way.
        </p>

        <h2 className="mt-12 font-serif text-3xl">The seven lines and what they traditionally mean</h2>
        <ul className="mt-4 space-y-3 text-muted-foreground leading-relaxed">
          <li><strong className="text-foreground">Life Line:</strong> curves around the base of the thumb. Traditionally linked to energy, vitality and major life changes. Its length does not show how long you will live.</li>
          <li><strong className="text-foreground">Heart Line:</strong> runs across the upper palm. Traditionally linked to emotions, affection and how you relate to others.</li>
          <li><strong className="text-foreground">Head Line:</strong> runs across the middle of the palm. Traditionally linked to thinking style, focus and curiosity.</li>
          <li><strong className="text-foreground">Fate Line:</strong> runs up the centre of the palm toward the middle finger. Traditionally linked to career direction and sense of purpose. Not everyone has one.</li>
          <li><strong className="text-foreground">Marriage Line:</strong> short horizontal lines on the edge of the palm below the little finger. Traditionally linked to close relationships and commitment.</li>
          <li><strong className="text-foreground">Sun Line:</strong> a vertical line below the ring finger. Traditionally linked to creativity and recognition.</li>
          <li><strong className="text-foreground">Health Line:</strong> a line running toward the base of the little finger. Traditionally linked to general wellbeing. It is not medical advice, so please see a doctor for any health concern.</li>
        </ul>

        <h2 className="mt-12 font-serif text-3xl">How to take a good palm photo</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed">
          <li>Use natural light or a bright room, and avoid harsh flash and shadows.</li>
          <li>Open your hand flat with the fingers slightly apart.</li>
          <li>Hold the camera straight above your palm so the lines stay in focus.</li>
          <li>Fill the frame with your palm, from the wrist to the fingertips.</li>
          <li>The tool asks for your right palm. Upload a new photo if the first one is blurry.</li>
        </ul>

        <h2 className="mt-12 font-serif text-3xl">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-serif text-xl">Can palm reading predict my future?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. Palm reading is a tradition for reflection and entertainment. Treat the results as ideas to think about, not facts.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Which hand should I upload?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">The tool asks for a clear photo of your right palm.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Why could not my photo be analyzed?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              The photo may be blurry, too dark or not show the whole palm. Try again with good light and a flat, open hand.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is the AI palm reading free?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Yes. You can upload a palm photo and read the results for free.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">What happens to my photo?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Your photo is sent to an AI service to read the lines, and a copy is stored on our servers. Please read our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> for details.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Want a number-based reading too?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Try the <Link to="/tools/numerology-calculator" className="text-primary underline">numerology calculator</Link> or the <Link to="/tools/lo-shu-grid" className="text-primary underline">Lo Shu grid</Link>.
            </p>
          </div>
        </div>
      </section>
    </ToolShell>
  );
}